import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { UserInviteModal } from "../../../components/modals/UserInviteModal";
import { MDBCheckbox, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import { CustomSelect } from "../../../components/CustomSelect";
import { TextInput } from "../../../components/core/inputs/TextInput";
import { Alert } from "../../../components/Alert";

export interface SubcompanyEditProps {
    companyId: number;
    canEditSubcompany: boolean;
    canDeleteSubcompany: boolean;
    canViewUsers: boolean;
    canAddUser: boolean;
    onClose: () => void;
    onEditDone: () => void;
    onDelete: () => void;
}

export const SubcompanyEdit = ({companyId, canEditSubcompany, canDeleteSubcompany, canViewUsers, canAddUser, onClose, onEditDone, onDelete}: SubcompanyEditProps) => {
    const [company, setCompany] = useState<any|null>(null);
    const [name, setName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [countries, setCountries] = useState<any[]>([]);
    const [compCountry, setCompCountry] = useState<any|null>(null);
    const [changed, setChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [area, setArea] = useState('general'); // general, !!users - delete!!, addUser, viewUser
    const [viewUser, setViewUser] = useState<any|null>(null);
    const [editError, setEditError] = useState('');
    const [inviteError, setInviteError] = useState('');
    const [selectedRightsId, setSelectedRightsId] = useState<number|null>(null);
    const [rights, setRights] = useState<any[]>([]);
    const [allRights, setAllRights] = useState<any[]>([]);
    const [invitationURL, setInvitationURL] = useState('');
    const [userInheritChange, setUserInheritChange] = useState(false);
    const [inviteUser, setInviteUser] = useState(false);
    const { t } = useTranslation();

    const onChange = useCallback((key: string, val: any) => {
        setCompany(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
    }, []);

    const fetch = useCallback(async () => {
        setChanged(false);
        setCompany(null);
        setCompCountry(null);
        setArea('general');
        setRights([]);
        setSelectedRightsId(null);
        setUserInheritChange(false);

        try {
            setCountries(await API.GET('/api/config/countries'));
            const rs = (await API.GET('/api/rights')).rights;
            setRights(rs.filter(r => r.private));
            setAllRights(rs);
            const idx = rs.findIndex(v => v.Name == 'Admin');
            if (idx > 0) setSelectedRightsId(rs[idx].id);
            else if (rs.length > 0) setSelectedRightsId(rs[0].id);
        }catch(err: any){
            setInviteError(err.message);
        }

        try {
            const comp = await API.GET('/api/companies/subcompanies/'+companyId);
            setCompany(comp);
            setName(comp.name);
        }catch(err){
            setArea('error');
            setCompany({});
        }
    }, [companyId]);

    useEffect(() => {
        fetch();
    }, [companyId]);

    useEffect(() => {
        if (Array.isArray(countries) && company){
            const country = countries.find(c => c.code === company.country);
            if (country) setCompCountry(country);
        }
    }, [countries, company]);

    useEffect(() => {
        setInviteEmail('');
        setInviteError('');
        setInvitationURL('');
        setEditError('');
    }, [company, area]);

    const submit = useCallback(async () => {
        setLoading(true);
        try {
            await API.PATCH('/api/companies/subcompanies/'+companyId, {
                name: company.name,
                CIF: company.CIF,
                maxRightsId: company.maxRightsId?.id,
                country: company.country
            });
            onEditDone();
        }catch(err: any){
            setEditError(err.message);
        }finally{
            setLoading(false);
            setChanged(false);
        }
    }, [company, companyId]);

    const invite = useCallback(async () => {
        setLoading(true);
        setInviteError('');
        setInvitationURL('');
        try {
            const { invitationId } = await API.POST('/api/users/invite', {
                email: inviteEmail,
                companyId: companyId,
                rightsId: selectedRightsId
            });
            setInviteEmail('');
            setInvitationURL(`${window.location.origin}/join?invitationId=${encodeURIComponent(invitationId)}`);
        }catch(err: any){
            setInviteError(err.message);
        }
    }, [inviteEmail, companyId, selectedRightsId]);

    const updateInheritRights = useCallback(async () => {
        setUserInheritChange(false);
        try {
            await API.PATCH(`/api/companies/subcompanies/${companyId}/u/${viewUser.id}`, { inheritRights: viewUser.inheritRights });
        }catch(err: any){
            setEditError(err.message);
        }finally{
            fetch();
        }
    }, [companyId, viewUser]);

    if (!company) return <></>;

    return (
        <div className="d-flex flex-column overflow-hidden card">
            <UserInviteModal
                subcompanyId={companyId}
                onClose={() => setInviteUser(false)}
                open={inviteUser}
            />
            
            <div className="d-flex flex-column overflow-hidden card-body">
                
                <div className="d-flex flex-column overflow-auto pe-1">
                    <div className="d-flex flex-column w-100 justify-content-center text-center">
                        <MDBIcon icon="fas fa-building" size="4x" className="mb-2"/>
                        <h5>{name}</h5>
                    </div>
                    {area === 'general' && (<>
                        {canEditSubcompany && <>
                            <TextInput label={t('name')} icon="pen-to-square" value={company.name} onValueChange={val => onChange('name', val)}/>
                            <TextInput label={t('cif')} icon="briefcase" value={company.CIF} onValueChange={val => onChange('CIF', val)}/>
                            <div className="d-flex mb-2 flex-row align-items-center w-100">
                                <MDBIcon icon="fas fa-earth-europe" fixed className="me-2"/>
                                <CustomSelect w100 title={t('country')} search={true} items={countries.map(c => ({ val: c.code, title: `${c.country}`, icon: `flag-icon flag-icon-${c.code?.toLocaleLowerCase()}`}))} selected={company.country} onSelect={code => {setCompany(comp => ({...comp, country: code})); setChanged(true); }} />
                            </div>
                            <div className="d-flex mb-2 flex-row align-items-center w-100">
                                <MDBIcon icon="fas fa-briefcase" fixed className="me-2"/>
                                <CustomSelect w100 title={t('permissions')} items={allRights.map(p => ({ val: p.id, title: p.Name }))} selected={company.maxRightsId?.id} onSelect={id => {setCompany(c => ({...c, maxRightsId: {...c.maxRightsId, id: id}})); setChanged(true); }} />
                            </div>
                        </>}
                        {!canEditSubcompany && <>
                            <ul className="list-group list-group-light p-1">
                                <li className="list-group-item p-1"><MDBIcon icon="fas fa-pencil" fixed className="me-1"/><span className="fw-bold me-1">{t('name')}:</span>{company.name}</li>
                                <li className="list-group-item p-1"><MDBIcon icon="fas fa-briefcase" fixed className="me-1"/><span className="fw-bold me-1">{t('cif')}:</span>{company.CIF}</li>
                                <li className="list-group-item p-1"><MDBIcon icon="fas fa-earth-europe" fixed className="me-1"/><span className="fw-bold me-1">{t('country')}:</span>
                                    {compCountry ? <span>{compCountry.country}</span> : <span>?</span>}
                                </li>
                            </ul>
                        </>}
                        {canViewUsers && <>
                            <div className="d-flex flex-row ms-2 mb-2 align-items-center">
                                <h5 className="m-0 me-1 px-0 py-0 m-0">{t('users')}</h5>
                                <span className="d-flex flex-fill"></span>
                            </div>
                            
                            {(
                                <div className="d-flex justify-content-start align-items-center" style={{flexWrap: 'wrap'}}>
                                    {company.users.map((u, i) => (
                                        <div className="d-inline-block mb-1 " key={i} role="button" onClick={() => { setViewUser(u); setArea('viewUser'); }} title={u.inheritRights ? t('user_inherits_rights') : ''}>
                                            <div className={"chip-custom mb-0 " + (u.inheritRights ? 'list-group-item-warning' : '')} style={{background: u.inheritRights ? '' : '#f1f1f1'}}>
                                                <span className="me-1" style={{color: 'rgb(79, 79, 79)'}}><MDBIcon icon="fas fa-circle-user" size="2x"/></span>
                                                <span className={"chip-text-custom" + (u.pending ? ' fst-italic':'')} style={{lineHeight: 1, whiteSpace: 'nowrap'}}>
                                                    {u.pending && <MDBIcon icon="fas fa-hourglass" color="primary" className="me-1"/>}
                                                    {u.pending ? u.mail : (u.Name || u.mail)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {canAddUser &&
                                        <span className="btn text-white btn-floating btn-sm bg-primary text-center mb-1" role="button" onClick={() => { setInviteUser(true); }}>
                                            <span className="d-flex justify-content-center align-items-center w-100 h-100"><MDBIcon icon="fas fa-plus" size="lg"/></span>
                                        </span>
                                    }      
                                </div>
                            )}
                            {company.users.length === 0 && (
                                <span className="mb-3 fst-italic">{t('no_users')}</span>
                            )}
                        
                        </>}
                        
                        
                        <div className="d-flex flex-row w-100 mt-2">
                            {canEditSubcompany && <button className="btn btn-primary flex-fill me-1" disabled={!changed || loading} onClick={submit}>{t('save')}</button> }
                            {canDeleteSubcompany && <button className="btn btn-danger flex-fill" disabled={loading} onClick={onDelete}>{t('delete')}</button> }
                        </div>
                        

                        { editError && <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                            { editError }
                        </div> }
                    
                    </>)}

                    {area === 'viewUser' && (<>
                        <span className="position-absolute" style={{left: 15, top: 10}} onClick={() => setArea('general')}><MDBIcon icon="fas fa-arrow-left-long" size="lg"/></span>
                        {viewUser.pending && <span className="w-100 d-flex justify-content-center"><img src="/time.svg" width={256} height={256}/></span>}
                        <ul className="list-group list-group-light p-1">
                            {!viewUser.pending && <li className="list-group-item p-1"><MDBIcon icon="fas fa-id-card" fixed className="me-1"/><span className="fw-bold me-1">{t('name')}:</span>{viewUser.Name}</li>}
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-envelope" fixed className="me-1"/><span className="fw-bold me-1">{t('email')}:</span>{viewUser.mail}</li>
                            {!viewUser.pending && <li className="list-group-item p-1"><MDBIcon icon="fas fa-mobile-alt" fixed className="me-1"/><span className="fw-bold me-1">{t('phone')}:</span>{viewUser.phone}</li>}
                            <li className="list-group-item p-1 d-flex flex-row align-items-center">
                                <MDBIcon icon="fas fa-briefcase" fixed className="me-1"/>
                                <span className="fw-bold me-1">{t('permissions')}:</span>{viewUser.rights?.Name||''}
                                {(canEditSubcompany && canAddUser) &&
                                    <MDBCheckbox label={t('inherit_rights')} id="inherit" type="checkbox" checked={viewUser.inheritRights}
                                        onChange={ev => {setViewUser(usr => ({...usr, inheritRights: ev.target?.checked || false})); setUserInheritChange(true);  }}
                                    />
                                }
                                {userInheritChange &&
                                    <span className="btn btn-link ms-2 p-1" onClick={updateInheritRights}>{t('save')}</span>
                                }
                            </li>
                        </ul>

                    </>)}

                    {area === 'error' && <Alert type="danger" message={t('no_company')}/>}
                </div>
                
                
            </div>

            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
        </div>
        
    )
}